// Footer.tsx — v2
import React from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import SpinningLogo from './SpinningLogo'

const PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || 'https://stocksignalspro.example.com'

interface FooterProps { dark: boolean }

const Footer: React.FC<FooterProps> = () => {
  const year = new Date().getFullYear()

  const cols = [
    {
      title: 'Products',
      links: [
        { label: 'StockSignals Pro', href: PRODUCT_URL, external: true, disabled: false },
        { label: 'DataAgents (Soon)', href: '#',         external: false, disabled: true },
        { label: 'Pricing',          href: '/pricing',   external: false, disabled: false },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Home',       href: '/',        external: false, disabled: false },
        { label: 'Contact Us', href: '/contact', external: false, disabled: false },
        { label: 'Blog',       href: '#',        external: false, disabled: true  },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', href: '/terms',             external: false, disabled: false },
        { label: 'Privacy Policy',   href: '/privacy',           external: false, disabled: false },
        { label: 'Cookie Policy',    href: '/privacy#cookies',   external: false, disabled: false },
      ],
    },
  ]

  return (
    <footer
      style={{
        background: "var(--c-surface)",
        borderTop: "1px solid var(--c-border)",
      }}
    >
      <div className='max-w-7xl mx-auto px-6 py-16'>
        <div className='grid grid-cols-1 md:grid-cols-4 gap-12 mb-12'>
          {/* Brand */}
          <div className='space-y-4'>
            <SpinningLogo size='sm' textSize='text-lg' />
            <p className='text-md leading-relaxed' style={{ color: "var(--c-text-3)" }}>
              Enterprise-grade data management and agentic analytics platform powered by AI.
            </p>
            <div className='flex items-center gap-2'>
              <div
                className='w-2 h-2 rounded-full'
                style={{
                  background: "var(--c-green)",
                  animation: "driftOrb 2s ease-in-out infinite",
                }}
              />
              <span
                className='text-xs'
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--c-text-3)",
                  letterSpacing: "0.06em",
                }}
              >
                ALL SYSTEMS OPERATIONAL
              </span>
            </div>
          </div>

          {/* Link columns */}
          {cols.map((col) => (
            <div key={col.title}>
              <h3
                className='text-xs font-bold uppercase tracking-widest mb-5'
                style={{ fontFamily: "var(--font-mono)", color: "var(--c-text-3)" }}
              >
                {col.title}
              </h3>
              <ul className='space-y-3'>
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.disabled ? (
                      <span
                        className='text-sm'
                        style={{ color: "var(--c-text-3)", cursor: "not-allowed" }}
                      >
                        {link.label}
                      </span>
                    ) : link.external ? (
                      <a
                        href={link.href}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center gap-1.5 text-sm transition-colors duration-150'
                        style={{ color: "var(--c-text-2)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-text)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-text-2)")}
                      >
                        {link.label}
                        <ExternalLink className='w-3 h-3' />
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className='text-sm transition-colors duration-150'
                        style={{ color: "var(--c-text-2)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-text)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-text-2)")}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className='pt-8 flex flex-col md:flex-row items-center justify-between gap-4'
          style={{ borderTop: "1px solid var(--c-border)" }}
        >
          <p className='text-sm' style={{ color: "var(--c-text-3)" }}>
          Copyright © 2023 - {year} Larxnet. All rights reserved.{" "}
          <span style={{ opacity: 0.7 }}>· {import.meta.env.VITE_APP_VERSION || "dev"}</span>
          </p>
          <div className='flex items-center gap-6'>
            <Link
              to='/terms'
              className='text-xs transition-colors duration-150'
              style={{ color: "var(--c-text-3)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-text)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-text-3)")}
            >
              Terms
            </Link>
            <Link
              to='/privacy'
              className='text-xs transition-colors duration-150'
              style={{ color: "var(--c-text-3)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-text)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-text-3)")}
            >
              Privacy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer
